import { oneDecimalLabel, POLICY_RULE_LEVELS, type PolicyRuleLevel } from "@hakili/domain-types";
import { formatQuantity, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_TEXT_2,
  Button,
  Callout,
  Chip,
  GuardedButton,
  NumberStepper,
  SegmentedControl,
  Toast,
  ToastStack,
} from "@hakili/ui";
import { AlertTriangle, ChevronRight, Info, Lock, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { COMPOSER_WARN_ICON } from "./composer.js";
import { DASH, fill } from "./dossiers.js";
import { PolicyCard, RuleRow } from "./politique-parts.js";
import { FirstPolicy } from "./politique-premiere.js";

/**
 * Round `L-5`, `M1` (`P3-BKW-005`, `P0-PRM-035`, WF-BNK-26, 03 `DECISION ADDED 12`): **« Politique »,
 * the chef's own screen**, `hakili-bank.jsx`'s `PolitiqueScreen` , the one place the delegation
 * threshold is editable, the analyst's copy read-only with the composer's link.
 *
 * Every figure is the door's (`GET /api/v1/bank/policy`): the grid, the threshold, the two ages
 * and the quotité are the policy's own columns (05 §2.6.7) and are written back whole by the
 * chef's door (`PUT`, `P1-LND-036`'s); the LTV's limit is HAKILI's configuration of the bank
 * (§2.6.7 amended at `L-1b`, `bank_criteria.ltv_max_pct`) and is printed read-only with that
 * reason in its title; the minimum employment seniority has no column and no rule anywhere, so it
 * stays a dash with its reason (R81). The level each rule carries is the composer's own constant
 * (`POLICY_RULE_LEVELS`, one place for the gaps and this screen), not a column , the selectors
 * read it and are disabled with that reason.
 *
 * The screen composes nothing: what it shows is what it read, what it sends is what the chef
 * typed, and the door answers with the grid replaced whole.
 */
export interface PolicyView {
  readonly rateTolerancePct: string;
  readonly durationMaxMonths: number;
  readonly vehicleAgeMaxYears: number;
  readonly vehicleAgeAtMaturityMaxYears: number;
  readonly taegCeilingPct: string;
  readonly delegationThresholdAmount: number;
  readonly assignableSharePct: number;
  /** `P1-BKW-055` (2b): « Le chef du crédit peut valider les dossiers qu'il instruit », as the door serves it (default `false`). */
  readonly creditHeadValidatesOwnFiles: boolean;
  readonly gradeRates: readonly { readonly grade: string; readonly ratePct: string }[];
}

/**
 * What the chef's `PUT` carries: the read's shape, **except that `creditHeadValidatesOwnFiles` rides ONLY when the chef changed the
 * switch** (the door leaves a stored value as it stands when the field is omitted, so a save that never touched the switch cannot
 * overwrite another chef's choice).
 */
export type PolicyWrite = Omit<PolicyView, "creditHeadValidatesOwnFiles"> & {
  readonly creditHeadValidatesOwnFiles?: boolean;
};

export interface PolicyRead {
  readonly policy: PolicyView | null;
  /** `KIT-TOAST-D2` 3a: the grid's highest row, served (`null` with no grid). */
  readonly capacityRowGrade?: string | null;
  readonly capacityRatePct?: string | null;
  /** Hakili's limit on the bank, read beside the policy; `null` when none is configured. */
  readonly ltvMaxPct: number | null;
}

/**
 * The grid's four rows, in the order the drawing gives them. A grade the policy has no rate for is a row with
 * an EMPTY rate (D-1021-4, no-pre-filled-rate ruling): the screen never invents a number, the chef types it.
 */
const GRADES = ["A+", "A", "B+", "B"] as const;
/** What the door takes (`ONE_DECIMAL` in `lending/domain/policy.ts`): one or two digits and exactly one decimal. */
const WIRE_RATE = /^\d{1,2}\.\d$/u;

/** A typed rate in the door's shape: « 10 » and « 10. » are « 10.0 »; `null` when nothing is typed. */
const wireRate = (typed: string): string | null => {
  if (typed === "") return null;
  const bare = typed.endsWith(".") ? typed.slice(0, -1) : typed;
  return bare.includes(".") ? bare : `${bare}.0`;
};

/**
 * `GRADE-C` (D-1024-3, D-1025-7): the grid's optional fifth row, a PLAIN FIELD like the other four (the designer's triage, frame 90:
 * « the empty field is the empty state, « Ajouter une ligne C » is not built, and « Retirer la ligne C » is clearing the field »).
 * It is NOT one of `GRADES`: an empty C field is not a missing letter, the banner and its count read the four only (BANK-IDS-2,
 * the server's `GRADE_LADDER`). Nothing is typed for the chef: the field opens empty, never on a rate of its own.
 */
const GRADE_C = "C";

/** D2 `pol`: the value an empty rate's first press writes (`11.0`), clamped to the ceiling. */
const RATE_START = 11;
/** D2 `pol`: the delegation threshold moves by half a million, and is never below one. */
const THRESHOLD_STEP = 500_000;
/** « 10,4 % »: one decimal, the comma, a no-break space before the sign. */
const rateWords = (value: number): string => `${(wireRate(String(value)) ?? "").replace(".", ",")}${NBSP}%`;
/** D2 `screens2.js` prints « 14 % » for the TAEG ceiling: a whole number carries no « ,0 ». */
const wholeOrComma = (wire: string): string =>
  wire.endsWith(".0") ? wire.slice(0, -2) : wire.replace(".", ",");
const thresholdWords = (value: number): string => `${formatQuantity(value)}${NBSP}FCFA`;

interface Draft {
  readonly gradeRates: readonly { readonly grade: string; readonly ratePct: string }[];
  /** What is typed in the C field, in the wire's dot-decimal; empty is no C rate. */
  readonly cRate: string;
  readonly delegationThresholdAmount: string;
  readonly vehicleAgeMaxYears: string;
  readonly vehicleAgeAtMaturityMaxYears: string;
  readonly assignableSharePct: string;
  /** `P1-BKW-055`: the switch as it stands on screen. */
  readonly creditHeadValidatesOwnFiles: boolean;
}

const draftOf = (policy: PolicyView): Draft => ({
  gradeRates: GRADES.map((grade) => ({
    grade,
    ratePct: policy.gradeRates.find((one) => one.grade === grade)?.ratePct ?? "",
  })),
  cRate: policy.gradeRates.find((one) => one.grade === GRADE_C)?.ratePct ?? "",
  delegationThresholdAmount: String(policy.delegationThresholdAmount),
  vehicleAgeMaxYears: String(policy.vehicleAgeMaxYears),
  vehicleAgeAtMaturityMaxYears: String(policy.vehicleAgeAtMaturityMaxYears),
  assignableSharePct: String(policy.assignableSharePct),
  creditHeadValidatesOwnFiles: policy.creditHeadValidatesOwnFiles,
});

/** A typed rate as whole tenths of a point (« 11,2 » is 112), `null` when nothing valid is typed: no float is compared. */
const tenthsOf = (typed: string): number | null => {
  const wire = wireRate(typed);
  return wire !== null && WIRE_RATE.test(wire) ? Number(wire.replace(".", "")) : null;
};
/** Whole tenths back to the wire's dot-decimal (112 is « 11.2 »). */
const wireOfTenths = (tenths: number): string => `${String(Math.trunc(tenths / 10))}.${String(tenths % 10)}`;
/** The highest of the four standard rows' typed rates in tenths, `null` when none is typed. */
const highestOfFour = (draft: Draft): number | null => {
  const values = draft.gradeRates
    .map((one) => tenthsOf(one.ratePct))
    .filter((value): value is number => value !== null);
  return values.length === 0 ? null : Math.max(...values);
};

/**
 * `GRADE-C`, frame 124, and `KIT-TOAST-D2` 3b/3c (D2 `5365fd20`): what a save says. **The door says it** (D-1020-7): `capacityRowGrade` and
 * `capacityRatePct` are the grid's highest row as the server derives it (the rule Capacité uses); the screen compares no rate. The toast
 * only confirms (`PolicyNotice`), and a save that makes row C the highest leaves a callout that stays (`PolicySaved`).
 */
export type PolicyNotice = "standard" | "capacityC" | "first";
export interface PolicySaved {
  readonly capacityRowGrade: string | null;
  readonly capacityRatePct: string | null;
}
/** « 11.4 » is « 11,4 % » (a no-break space before the sign), as the grid prints a rate. */
const rateOfWire = (wire: string): string => `${wire.replace(".", ",")}\u00a0%`;

const sameDraft = (a: Draft, b: Draft): boolean =>
  a.delegationThresholdAmount === b.delegationThresholdAmount &&
  a.vehicleAgeMaxYears === b.vehicleAgeMaxYears &&
  a.vehicleAgeAtMaturityMaxYears === b.vehicleAgeAtMaturityMaxYears &&
  a.assignableSharePct === b.assignableSharePct &&
  a.creditHeadValidatesOwnFiles === b.creditHeadValidatesOwnFiles &&
  a.cRate === b.cRate &&
  a.gradeRates.length === b.gradeRates.length &&
  a.gradeRates.every((one, index) => {
    const other = b.gradeRates[index];
    return one.grade === other?.grade && one.ratePct === other.ratePct;
  });

/** The drawing's three words for a level; the key each carries in the catalogue. */
const LEVEL_KEYS: Readonly<Record<PolicyRuleLevel, Parameters<typeof t>[0]>> = {
  eliminatoire: "bank.politique.niveauEliminatoire",
  chefCredit: "bank.politique.niveauChef",
  comiteCredit: "bank.politique.niveauComite",
};

/** « A », « B+ et B », « A, B+ et B »: the French list form, commas then « et » before the last. */
export const frenchList = (items: readonly string[]): string =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} ${t("bank.politique.et")} ${items.at(-1) ?? ""}`;

function LevelChip(props: {
  readonly level: PolicyRuleLevel;
  readonly testId: string;
  /** The rule this level belongs to: the chip's accessible name (`DX-B-013`). */
  readonly rule: string;
}): React.JSX.Element {
  // The levels are the composer's constants (05 §2.6.7 amended): shown locked, the reason in the title. Neutral, never a warning.
  const reason = t("bank.politique.niveauConstantMotif");
  return (
    <span
      title={reason}
      role="img"
      aria-label={`${fill("bank.politique.niveauDe", { regle: props.rule })} : ${t(LEVEL_KEYS[props.level])}`}
      data-level={props.level}
      data-testid={props.testId}
      data-kind="chip-label"
      className="inline-flex items-center gap-1"
    >
      <Chip
        tone={props.level === "eliminatoire" ? "alerte" : "attention"}
        className={props.level === "comiteCredit" ? "!bg-indigo-50 !text-indigo-700" : ""}
      >
        {props.level === "eliminatoire" ? <Lock className="inline h-3 w-3" aria-hidden="true" /> : null}
        {t(LEVEL_KEYS[props.level])}
      </Chip>
    </span>
  );
}

/**
 * `POLITIQUE-STEPPERS` (D2 `screens2.js:99`, `step()`): minus, figure, plus, the figure in the monospaced face. An empty rate reads a dash and
 * the first press of either button writes the starting value (D2's `pol`: `11.0`), so a row with no rate is never a text box to fill.
 * The bounds are the door's (a rate is 0,1 to the TAEG ceiling, a threshold is whole half-millions, an age is a year or more, a share is
 * 1 to 100): the control cannot write what the door would refuse, so a typed-rate error line has nothing left to say.
 */
function PolicyStepper(props: {
  readonly name: string;
  readonly value: number | null;
  readonly start: number;
  readonly min: number;
  readonly max?: number;
  readonly step: number;
  readonly format: (value: number) => string;
  readonly busy: boolean;
  readonly onChange: (value: number) => void;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <NumberStepper
      label={props.name}
      decreaseLabel={fill("bank.politique.baisser", { nom: props.name })}
      increaseLabel={fill("bank.politique.monter", { nom: props.name })}
      value={props.value ?? props.start}
      // While empty, the figure is a dash and either press writes the start value: no bound applies yet (a ceiling at the start would
      // otherwise grey the plus before the first press).
      {...(props.value === null ? {} : { min: props.min })}
      {...(props.value === null || props.max === undefined ? {} : { max: props.max })}
      step={props.step}
      format={props.value === null ? () => t("bank.feuille.vide") : props.format}
      onChange={(next) => {
        if (!props.busy) props.onChange(props.value === null ? props.start : next);
      }}
      testId={props.testId}
    />
  );
}

/** D2's `.mono` figure, « 10,4 % »: the unit rides in the same text, joined by a no-break space. */
function Figure(props: {
  readonly children: string;
  readonly testId?: string;
  readonly title?: string;
}): React.JSX.Element {
  return (
    <b
      data-testid={props.testId}
      title={props.title}
      className="font-mono text-sm font-semibold tabular-nums text-gray-900"
    >
      {props.children}
    </b>
  );
}

const NBSP = "\u00a0";

export function Politique(props: {
  readonly read: PolicyRead | null;
  /** The analyst's copy (03 `DECISION ADDED 12`): every control read-only. */
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onSave: (policy: PolicyWrite) => void;
  /** `GRADE-C` frame 124: the toast after a save the door accepted; `null` or absent, none. */
  readonly notice?: PolicyNotice | null;
  /** `KIT-TOAST-D2` 3b: the answer of the save just made (the door's highest row); `null` or absent, no callout. */
  readonly saved?: PolicySaved | null;
  /** D2 (122): the analyst's crumb « Offre indicative › Politique », back to the file she came from; absent when she did not. */
  readonly origin?: { readonly onOpen: () => void };
}): React.JSX.Element {
  const policy = props.read?.policy ?? null;
  const [draft, setDraft] = useState<Draft | null>(policy === null ? null : draftOf(policy));
  // The policy the draft was cut from: while a NEW read is landing the draft still belongs to the old one, and is not « an edit ».
  const [draftFrom, setDraftFrom] = useState<PolicyView | null>(policy);
  useEffect(() => {
    setDraft(policy === null ? null : draftOf(policy));
    setDraftFrom(policy);
  }, [policy]);
  // `KIT-TOAST-D2` 3b: the callout of a save stays until « Compris », a NEW edit, or leaving the screen (local view state, per save).
  const [noteEndedFor, setNoteEndedFor] = useState<PolicySaved | null>(null);
  const edited =
    policy !== null && draft !== null && draftFrom === policy && !sameDraft(draft, draftOf(policy));
  const note = props.saved ?? null;
  useEffect(() => {
    if (edited && note !== null) setNoteEndedFor(note);
  }, [edited, note]);
  const noteShown =
    !props.readOnly &&
    note !== null &&
    note.capacityRowGrade === "C" &&
    note.capacityRatePct !== null &&
    noteEndedFor !== note &&
    !edited;

  if (props.read === null) {
    return <p className="p-6 text-sm text-gray-500">{t("bank.session.chargement")}</p>;
  }
  // `POLICY-FIRST` Screen A: the chef of a bank with no policy defines it, eight values typed by him (the analyst's copy keeps frame 169).
  if (policy === null && !props.readOnly) {
    return <FirstPolicy busy={props.busy} refusal={props.refusal} onSave={props.onSave} />;
  }
  const changed = edited;
  // The grid the chef would write: only the rates TYPED, in grade order. A fully empty grid cannot be saved.
  const ceilingPct = policy === null ? 0 : Number(policy.taegCeilingPct);
  const typedRates = draft === null ? [] : draft.gradeRates.filter((one) => one.ratePct !== "");
  const cTypedRate = draft === null ? "" : draft.cRate;
  // The banner reads what the door answered, not what is being typed.
  const missing = (policy === null ? [] : draftOf(policy).gradeRates)
    .filter((one) => one.ratePct === "")
    .map((one) => one.grade);
  const patch = (next: Partial<Draft>): void => {
    if (draft !== null) setDraft({ ...draft, ...next });
  };
  const rateField = (
    one: { readonly grade: string; readonly ratePct: string },
    index: number,
  ): React.JSX.Element => {
    const key = one.grade.replace("+", "plus");
    if (props.readOnly) {
      // The analyst's copy: a figure, or the words, never a box that looks editable.
      return one.ratePct === "" ? (
        <span
          data-testid={`bank-politique-taux-indice-${key}`}
          className="block whitespace-nowrap text-right text-sm text-gray-500"
        >
          {t("bank.politique.tauxAbsentIndice")}
        </span>
      ) : (
        <Figure testId={`bank-politique-taux-${key}`}>{rateOfWire(one.ratePct)}</Figure>
      );
    }
    return (
      <PolicyStepper
        name={fill("bank.politique.nomTaux", { note: one.grade })}
        value={tenthsOf(one.ratePct) === null ? null : (tenthsOf(one.ratePct) ?? 0) / 10}
        start={Math.min(RATE_START, ceilingPct)}
        min={0.1}
        max={ceilingPct}
        step={0.1}
        format={rateWords}
        busy={props.busy}
        onChange={(next) => {
          if (draft !== null) {
            patch({
              gradeRates: draft.gradeRates.map((row, at) =>
                at === index ? { ...row, ratePct: wireRate(String(next)) ?? "" } : row,
              ),
            });
          }
        }}
        testId={`bank-politique-taux-${key}`}
      />
    );
  };
  const save = (): void => {
    if (policy === null || draft === null) return;
    props.onSave({
      rateTolerancePct: policy.rateTolerancePct,
      durationMaxMonths: policy.durationMaxMonths,
      taegCeilingPct: policy.taegCeilingPct,
      gradeRates: [
        ...typedRates.map((one) => ({ grade: one.grade, ratePct: wireRate(one.ratePct) ?? "" })),
        ...(cTypedRate === "" ? [] : [{ grade: GRADE_C, ratePct: wireRate(cTypedRate) ?? "" }]),
      ],
      delegationThresholdAmount: Number(draft.delegationThresholdAmount),
      vehicleAgeMaxYears: Number(draft.vehicleAgeMaxYears),
      vehicleAgeAtMaturityMaxYears: Number(draft.vehicleAgeAtMaturityMaxYears),
      assignableSharePct: Number(draft.assignableSharePct),
      // `P1-BKW-055`: the field rides only when the chef changed the switch.
      ...(draft.creditHeadValidatesOwnFiles === policy.creditHeadValidatesOwnFiles
        ? {}
        : { creditHeadValidatesOwnFiles: draft.creditHeadValidatesOwnFiles }),
    });
  };
  // `GRADE-C` (frames 97, 98, 122, 123, D2 `screens2.js:111`): the grid's optional fifth row, after B, in the same card. Empty it reads
  // « Pas de taux » beside « Ajouter une ligne C »; set it is a stepper beside « Retirer la ligne C ». The analyst reads the figure, or
  // « Aucun taux saisi ». D2's `pol-addC` writes the highest of the four (or 11,0) plus 0,2, here kept under the ceiling.
  const policyCeiling = policy === null ? "" : policy.taegCeilingPct;
  const cRow = (current: Draft): React.JSX.Element => {
    const cKey = GRADE_C;
    const others = highestOfFour(current);
    const cValue = current.cRate !== "" ? tenthsOf(current.cRate) : null;
    const aboveAll = cValue !== null && (others === null || cValue > others) ? cValue : null;
    const shown = (tenths: number): string => wireOfTenths(tenths).replace(".", ",");
    const control = props.readOnly ? (
      current.cRate !== "" ? (
        <Figure testId={`bank-politique-taux-${cKey}`}>{`${current.cRate.replace(".", ",")}${NBSP}%`}</Figure>
      ) : (
        <span data-testid={`bank-politique-taux-indice-${cKey}`} className="text-sm text-gray-500">
          {t("bank.politique.tauxAbsentIndice")}
        </span>
      )
    ) : cValue === null ? (
      <>
        <span data-testid="bank-politique-taux-indice-C" className="text-sm text-gray-500">
          {t("bank.politique.pasDeTaux")}
        </span>
        <Button
          variant="ghost"
          size="sm"
          data-testid="bank-politique-note-c-ajouter"
          onClick={() => {
            const ceilingTenths = tenthsOf(policyCeiling) ?? 0;
            patch({
              cRate: wireOfTenths(Math.min(Math.max(others ?? 0, RATE_START * 10) + 2, ceilingTenths)),
            });
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t("bank.politique.ajouterC")}
          </span>
        </Button>
      </>
    ) : (
      <>
        <PolicyStepper
          name={fill("bank.politique.nomTaux", { note: cKey })}
          value={cValue / 10}
          start={Math.min(RATE_START, ceilingPct)}
          min={0.1}
          max={ceilingPct}
          step={0.1}
          format={rateWords}
          busy={props.busy}
          onChange={(next) => {
            patch({ cRate: wireRate(String(next)) ?? "" });
          }}
          testId={`bank-politique-taux-${cKey}`}
        />
        <Button
          variant="ghost"
          size="sm"
          data-testid="bank-politique-note-c-retirer"
          onClick={() => {
            patch({ cRate: "" });
          }}
        >
          {t("bank.politique.retirerC")}
        </Button>
      </>
    );
    return (
      <div data-testid="bank-politique-note-c">
        <RuleRow
          label={fill("bank.politique.noteLigne", { note: cKey })}
          sub={t("bank.politique.noteCSous")}
          subTestId="bank-politique-note-c-legende"
          control={control}
        />
        {aboveAll !== null ? (
          <p
            role="note"
            data-testid="bank-politique-note-c-info"
            className="m-0 mt-1 flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2.5 text-sm text-gray-900"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-700" aria-hidden="true" />
            <span>
              {fill("bank.politique.noteCInfo", { taux: shown(aboveAll) })}
              {props.readOnly ? "" : ` ${t("bank.politique.noteCInfoRecalcul")}`}
            </span>
          </p>
        ) : null}
        {cValue === null ? null : (
          <p data-testid="bank-politique-note-c-aide" className={`m-0 mt-2.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
            <span className="font-semibold text-gray-900">{t("bank.politique.noteCAideTitre")}</span>{" "}
            {t("bank.politique.noteCAide")}
          </p>
        )}
      </div>
    );
  };
  // D2 (`dec(p.thr / 1e6, 1)`): « 5,0 M », one decimal, the comma.
  const thresholdMillions =
    draft === null || draft.delegationThresholdAmount === ""
      ? DASH
      : oneDecimalLabel(Number(draft.delegationThresholdAmount) / 1_000_000).replace(".", ",");
  const infoIcon = <Info className="h-5 w-5 text-indigo-700" aria-hidden="true" />;

  return (
    <div data-testid="bank-politique">
      {props.readOnly && props.origin !== undefined ? (
        <div
          className={`mb-3 flex items-center gap-1.5 ${BANK_FS_META} text-gray-600`}
          data-testid="bank-politique-fil"
        >
          <button
            type="button"
            onClick={props.origin.onOpen}
            className="hover:text-indigo-600"
            data-testid="bank-politique-fil-offre"
          >
            {t("bank.politique.filOffre")}
          </button>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <span className="font-medium text-gray-900">{t("bank.politique.filCourant")}</span>
        </div>
      ) : null}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">{t("bank.politique.titre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{t("bank.politique.sousTitre")}</p>
      </div>
      {props.readOnly ? (
        <Callout tone="info" className="mb-3" testId="bank-politique-lecture-seule" icon={infoIcon}>
          <strong className="font-semibold">{t("bank.politique.lectureSeuleTitre")}</strong>{" "}
          {t("bank.politique.lectureSeuleSuite")}
        </Callout>
      ) : null}
      {noteShown ? (
        <Callout tone="info" className="mb-3" testId="bank-politique-note-enregistree" icon={infoIcon}>
          <p className="m-0">
            <strong className="font-semibold">{t("bank.politique.noteEnregistreeTitre")}</strong>{" "}
            {fill("bank.politique.noteEnregistreeSuite", { taux: rateOfWire(note.capacityRatePct) })}
          </p>
          <p className="m-0 mt-1.5">
            <Button
              variant="ghost"
              size="sm"
              data-testid="bank-politique-note-enregistree-compris"
              onClick={() => {
                setNoteEndedFor(note);
              }}
            >
              {t("bank.politique.compris")}
            </Button>
          </p>
        </Callout>
      ) : null}
      {props.notice === undefined || props.notice === null || props.notice === "first" ? null : (
        <ToastStack>
          <Toast>
            {t(props.notice === "capacityC" ? "bank.politique.enregistreeC" : "bank.politique.enregistree")}
          </Toast>
        </ToastStack>
      )}
      {props.refusal === null ? null : (
        <p role="alert" className="mb-4 text-sm text-rose-600" data-testid="bank-politique-refus">
          {props.refusal}
        </p>
      )}

      {policy !== null && missing.length > 0 ? (
        <Callout
          tone="warn"
          className="mb-4"
          testId="bank-politique-banniere-grille"
          icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
        >
          <p className="m-0 font-semibold">
            {missing.length === GRADES.length
              ? t("bank.politique.grilleVideTitre")
              : fill(
                  missing.length === 1
                    ? "bank.politique.grillePartielleUne"
                    : "bank.politique.grillePartiellePlusieurs",
                  { notes: frenchList(missing) },
                )}
          </p>
          <p className="m-0">
            {missing.length === GRADES.length
              ? t(props.readOnly ? "bank.politique.grilleVideChef" : "bank.politique.grilleVideSaisir")
              : t(
                  props.readOnly
                    ? "bank.politique.grillePartielleChef"
                    : "bank.politique.grillePartielleSaisir",
                )}
          </p>
          {missing.length === GRADES.length ? (
            <>
              <p className="m-0 mt-3 font-semibold">{t("bank.politique.grilleVideCeQueTitre")}</p>
              <ul className="m-0 mt-1 list-disc space-y-1 pl-5">
                <li>
                  <span className="font-semibold">{t("bank.politique.grilleVideCapaciteTitre")}</span>
                  {t("bank.politique.grilleVideCapacite")}
                  <strong className="font-semibold">{t("bank.politique.grilleVideNonCalculee")}</strong>
                  {t("bank.politique.grilleVideCapaciteMilieu")}
                  <strong className="font-semibold">{t("bank.politique.grilleVideNoteIncomplete")}</strong>
                  {t("bank.politique.grilleVideCapaciteDeux")}
                  <strong className="font-semibold">{t("bank.politique.grilleVideAucune")}</strong>.
                </li>
                {(
                  [
                    ["Offre", "bank.politique.grilleVideOffreTitre", "bank.politique.grilleVideOffre"],
                    ["Taeg", "bank.politique.grilleVideTaegTitre", "bank.politique.grilleVideTaeg"],
                    ["Ecart", "bank.politique.grilleVideEcartTitre", "bank.politique.grilleVideEcart"],
                  ] as const
                ).map(([key, head, body]) => (
                  <li key={key}>
                    <span className="font-semibold">{t(head)}</span>
                    {t(body)}
                  </li>
                ))}
              </ul>
              <p className="m-0 mt-3">{t("bank.politique.grilleVideContinue")}</p>
            </>
          ) : (
            <>
              <p className="m-0 mt-3 font-semibold">{t("bank.politique.grillePartielleCeQueTitre")}</p>
              <ul
                className="m-0 mt-1 list-disc space-y-1 pl-5"
                data-testid="bank-politique-partielle-consequences"
              >
                <li>
                  <span className="font-semibold">{t("bank.politique.grillePartielleCapaciteTitre")}</span>
                  {t("bank.politique.grillePartielleCapacite")}
                </li>
                <li>
                  <span className="font-semibold">{t("bank.politique.grillePartielleOffreTitre")}</span>
                  {t("bank.politique.grillePartielleOffreAvant")}
                  {missing.join(` ${t("bank.politique.ou")} `)}
                  {t("bank.politique.grillePartielleOffreApres")}
                </li>
                <li>
                  <span className="font-semibold">{t("bank.politique.grillePartielleEcartTitre")}</span>
                  {t("bank.politique.grillePartielleEcart")}
                </li>
                <li>
                  <span className="font-semibold">{t("bank.politique.grillePartielleTaegTitre")}</span>
                  {t("bank.politique.grillePartielleTaeg")}
                </li>
              </ul>
              <p className="m-0 mt-3">{t("bank.politique.grillePartielleNormal")}</p>
            </>
          )}
        </Callout>
      ) : null}

      {/* D2 draws this callout on the policy screen WITH a policy (`screens2.js:118`), never on the first-policy state (designer's review 10b5e239, row 2). */}
      {policy === null ? null : (
        <Callout tone="info" className="mb-4" icon={infoIcon}>
          {t("bank.politique.banniere")}{" "}
          <strong className="font-semibold">{t("bank.politique.motEliminatoire")}</strong>
          {t("bank.politique.banniereEliminatoire")}{" "}
          <strong className="font-semibold">{t("bank.politique.motDerogeable")}</strong>
          {t("bank.politique.banniereDerogeable")}
        </Callout>
      )}

      {policy === null || draft === null ? (
        props.readOnly ? (
          // `POLICY-FIRST` Screen B, frame 169: the analyst's read-only copy keeps the product's line and adds D2's second sentence.
          <Callout tone="warn" testId="bank-politique-absente" icon={COMPOSER_WARN_ICON}>
            <p className="font-semibold">{t("bank.politique.absente")}</p>
            <p>{t("bank.politique.absenteLecture")}</p>
          </Callout>
        ) : (
          <p className="text-sm text-gray-500" data-testid="bank-politique-absente">
            {t("bank.politique.absente")}
          </p>
        )
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-4 min-[1100px]:grid-cols-2">
            <div className="grid gap-4">
              <PolicyCard
                testId="bank-politique-grille"
                title={t("bank.politique.grilleTitre")}
                sub={t("bank.politique.grilleSousTitre")}
              >
                {draft.gradeRates.map((one, index) => (
                  <RuleRow
                    key={one.grade}
                    label={fill("bank.politique.noteLigne", { note: one.grade })}
                    control={rateField(one, index)}
                  />
                ))}
                {cRow(draft)}
                <p className={`m-0 mt-2.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                  {t("bank.politique.grilleNote")}{" "}
                  {fill("bank.politique.plafondUemoa", { plafond: wholeOrComma(policy.taegCeilingPct) })}{" "}
                  {fill(
                    Number(policy.rateTolerancePct) >= 2
                      ? "bank.politique.tolerancePluriel"
                      : "bank.politique.tolerance",
                    { points: policy.rateTolerancePct.replace(".", ",") },
                  )}
                </p>
              </PolicyCard>

              <PolicyCard
                testId="bank-politique-delegation"
                title={t("bank.politique.delegationTitre")}
                sub={t("bank.politique.delegationSousTitre")}
              >
                <RuleRow
                  label={t("bank.politique.analyste")}
                  control={
                    <span className="text-sm text-gray-500">
                      {t("bank.politique.analysteAucunEngagement")}
                    </span>
                  }
                />
                <RuleRow
                  label={t("bank.politique.chefJusqua")}
                  control={
                    props.readOnly ? (
                      <Figure testId="bank-politique-seuil">
                        {`${formatQuantity(Number(draft.delegationThresholdAmount))}${NBSP}FCFA`}
                      </Figure>
                    ) : (
                      <PolicyStepper
                        name={t("bank.politique.nomSeuil")}
                        value={Number(draft.delegationThresholdAmount)}
                        start={THRESHOLD_STEP}
                        min={THRESHOLD_STEP}
                        step={THRESHOLD_STEP}
                        format={thresholdWords}
                        busy={props.busy}
                        onChange={(next) => {
                          patch({ delegationThresholdAmount: String(next) });
                        }}
                        testId="bank-politique-seuil"
                      />
                    )
                  }
                />
                <RuleRow
                  label={t("bank.politique.chefValideTitre")}
                  sub={t(
                    draft.creditHeadValidatesOwnFiles
                      ? "bank.politique.chefValideTexteOn"
                      : "bank.politique.chefValideTexteOff",
                  )}
                  subTestId="bank-politique-chef-valide-texte"
                  longSub
                  control={
                    props.readOnly ? (
                      <Figure testId="bank-politique-chef-valide-etat">
                        {t(
                          draft.creditHeadValidatesOwnFiles
                            ? "bank.politique.chefValideActive"
                            : "bank.politique.chefValideDesactive",
                        )}
                      </Figure>
                    ) : (
                      <span data-testid="bank-politique-chef-valide-bascule">
                        <SegmentedControl
                          label={t("bank.politique.chefValideTitre")}
                          value={draft.creditHeadValidatesOwnFiles ? "on" : "off"}
                          tone="ink"
                          options={[
                            {
                              value: "off",
                              label: t("bank.politique.chefValideDesactive"),
                              testId: "bank-politique-chef-valide-off",
                            },
                            {
                              value: "on",
                              label: t("bank.politique.chefValideActive"),
                              testId: "bank-politique-chef-valide-on",
                            },
                          ]}
                          onChange={(next) => {
                            patch({ creditHeadValidatesOwnFiles: next === "on" });
                          }}
                        />
                      </span>
                    )
                  }
                />
                <RuleRow
                  label={t("bank.politique.comite")}
                  sub={fill("bank.politique.comiteSous", { millions: thresholdMillions })}
                  subTestId="bank-politique-comite-au-dela"
                  longSub
                />
              </PolicyCard>
            </div>

            <div className="grid gap-4">
              <PolicyCard
                testId="bank-politique-vehicule"
                title={t("bank.politique.vehiculeTitre")}
                sub={t("bank.politique.vehiculeSousTitre")}
              >
                <RuleRow
                  label={t("bank.politique.ageOctroi")}
                  control={
                    props.readOnly ? (
                      <Figure testId="bank-politique-age-octroi">{`${draft.vehicleAgeMaxYears}${NBSP}${t("bank.politique.ans")}`}</Figure>
                    ) : (
                      <PolicyStepper
                        name={t("bank.politique.nomAgeOctroi")}
                        value={Number(draft.vehicleAgeMaxYears)}
                        start={1}
                        min={1}
                        step={1}
                        format={(value) => `${String(value)}${NBSP}${t("bank.politique.ans")}`}
                        busy={props.busy}
                        onChange={(next) => {
                          patch({ vehicleAgeMaxYears: String(next) });
                        }}
                        testId="bank-politique-age-octroi"
                      />
                    )
                  }
                  level={
                    <LevelChip
                      level={POLICY_RULE_LEVELS.vehicleAgeAtGrant}
                      testId="bank-politique-niveau-age-octroi"
                      rule={t("bank.politique.ageOctroi")}
                    />
                  }
                />
                <RuleRow
                  label={t("bank.politique.ageEcheance")}
                  control={
                    props.readOnly ? (
                      <Figure testId="bank-politique-age-echeance">{`${draft.vehicleAgeAtMaturityMaxYears}${NBSP}${t("bank.politique.ans")}`}</Figure>
                    ) : (
                      <PolicyStepper
                        name={t("bank.politique.nomAgeEcheance")}
                        value={Number(draft.vehicleAgeAtMaturityMaxYears)}
                        start={1}
                        min={1}
                        step={1}
                        format={(value) => `${String(value)}${NBSP}${t("bank.politique.ans")}`}
                        busy={props.busy}
                        onChange={(next) => {
                          patch({ vehicleAgeAtMaturityMaxYears: String(next) });
                        }}
                        testId="bank-politique-age-echeance"
                      />
                    )
                  }
                  level={
                    <LevelChip
                      level={POLICY_RULE_LEVELS.vehicleAgeAtMaturity}
                      testId="bank-politique-niveau-age-echeance"
                      rule={t("bank.politique.ageEcheance")}
                    />
                  }
                />
                <RuleRow
                  label={t("bank.politique.ltv")}
                  sub={t("bank.politique.ltvSous")}
                  subTestId="bank-politique-ltv-sous"
                  control={
                    // Hakili's limit on the bank, never the chef's to write here (05 §2.6.7 amended).
                    <span className="inline-flex items-center gap-1.5">
                      <Figure testId="bank-politique-ltv" title={t("bank.politique.ltvMotif")}>
                        {props.read.ltvMaxPct === null
                          ? DASH
                          : `${String(props.read.ltvMaxPct).replace(".", ",")}${NBSP}%`}
                      </Figure>
                      <Lock className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
                    </span>
                  }
                  level={
                    <LevelChip
                      level={POLICY_RULE_LEVELS.ltv}
                      testId="bank-politique-niveau-ltv"
                      rule={t("bank.politique.ltv")}
                    />
                  }
                />
                <RuleRow
                  label={t("bank.politique.dureeMax")}
                  sub={t("bank.politique.dureeMaxSous")}
                  control={
                    <span className="inline-flex items-center gap-1.5">
                      <Figure testId="bank-politique-duree-max">
                        {`${String(policy.durationMaxMonths)}${NBSP}${t("bank.politique.mois")}`}
                      </Figure>
                      <Lock className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
                    </span>
                  }
                />
                <p className={`m-0 mt-2 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                  {t("bank.politique.expertiseObligatoire")} {t("bank.politique.niveauxVerrouilles")}
                </p>
              </PolicyCard>

              <PolicyCard
                testId="bank-politique-emprunteur"
                title={t("bank.politique.emprunteurTitre")}
                sub={t("bank.politique.emprunteurSousTitre")}
              >
                <RuleRow
                  label={t("bank.politique.quotite")}
                  control={
                    props.readOnly ? (
                      <Figure testId="bank-politique-quotite">{`${draft.assignableSharePct}${NBSP}%`}</Figure>
                    ) : (
                      <PolicyStepper
                        name={t("bank.politique.nomQuotite")}
                        value={Number(draft.assignableSharePct)}
                        start={1}
                        min={1}
                        max={100}
                        step={1}
                        format={(value) => `${String(value)}${NBSP}%`}
                        busy={props.busy}
                        onChange={(next) => {
                          patch({ assignableSharePct: String(next) });
                        }}
                        testId="bank-politique-quotite"
                      />
                    )
                  }
                  level={
                    <LevelChip
                      level={POLICY_RULE_LEVELS.quotite}
                      testId="bank-politique-niveau-quotite"
                      rule={t("bank.politique.quotite")}
                    />
                  }
                />
                {/* No column and no rule carries the seniority today: said in words, with its reason, no chip (R81, D-1019-2). */}
                <RuleRow
                  label={t("bank.politique.anciennete")}
                  sub={t("bank.politique.ancienneteAucuneRegle")}
                  subTestId="bank-politique-anciennete"
                  subTitle={t("bank.politique.ancienneteMotif")}
                  muted
                />
                <p
                  className={`mb-1.5 mt-3.5 ${BANK_FS_META} font-semibold uppercase tracking-[0.12em] text-gray-600`}
                  data-meta
                >
                  {t("bank.politique.exigencesTitre")}
                </p>
                <ul className="m-0 grid list-none gap-2 p-0">
                  {(
                    [
                      "bank.politique.exigenceDomiciliation",
                      "bank.politique.exigenceAssurance",
                      "bank.politique.exigenceBulletins",
                    ] as const
                  ).map((key) => (
                    <li
                      key={key}
                      data-testid={`bank-politique-${key
                        .replace("bank.politique.", "")
                        .replace(/^exigence/u, "exigence-")
                        .toLowerCase()}`}
                      className="flex flex-wrap items-center gap-2 text-sm text-gray-900"
                    >
                      <Lock className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
                      <span>{t(key)}</span>
                      <span data-kind="chip-label">
                        <Chip tone="alerte">{t("bank.politique.niveauEliminatoire")}</Chip>
                      </span>
                    </li>
                  ))}
                </ul>
              </PolicyCard>
            </div>
          </div>

          {(changed || missing.length > 0) && !props.readOnly ? (
            <ActionBar label={t("bank.politique.barre")} testId="bank-politique-barre" className="mt-4">
              {/* D2 `actionbar`: the sentence FIRST (`.hint`, the whole row), the buttons under it. */}
              <p
                className={`m-0 basis-full ${BANK_FS_BODY} ${BANK_TEXT_2}`}
                data-testid="bank-politique-barre-phrase"
              >
                {t("bank.politique.pied")}
              </p>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(draftOf(policy));
                }}
                data-testid="bank-politique-annuler"
              >
                {t("bank.politique.annuler")}
              </Button>
              {typedRates.length === 0 && cTypedRate === "" ? (
                <GuardedButton
                  disabled
                  reason={t("bank.politique.saisirUnTaux")}
                  reasonDress="chip"
                  reasonIcon={<Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                  size="lg"
                  testId="bank-politique-enregistrer"
                >
                  {t("bank.politique.enregistrer")}
                </GuardedButton>
              ) : props.busy ? (
                <GuardedButton
                  disabled
                  reason={t("bank.politique.enregistrement")}
                  reasonTone="neutral"
                  reasonDress="chip"
                  reasonIcon={<Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                  size="lg"
                  testId="bank-politique-enregistrer"
                >
                  {t("bank.politique.enregistrer")}
                </GuardedButton>
              ) : (
                <GuardedButton size="lg" testId="bank-politique-enregistrer" onClick={save}>
                  {t("bank.politique.enregistrer")}
                </GuardedButton>
              )}
            </ActionBar>
          ) : props.readOnly ? null : (
            <p className="mt-4 px-1 text-sm text-gray-500" data-testid="bank-politique-pied">
              {t("bank.politique.piedCourt")}
            </p>
          )}
        </>
      )}
    </div>
  );
}
